import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';
import WebsiteCMS from './superadmin/WebsiteCMS';
import ContactInbox from './superadmin/ContactInbox';

const SuperAdminDashboard = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [language, setLanguage] = useState('english'); // Default to English
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [registrationRequests, setRegistrationRequests] = useState([]);
  const [requestsFilter, setRequestsFilter] = useState('pending');
  const [subscriptionPackages, setSubscriptionPackages] = useState([]);
  const [globalSettings, setGlobalSettings] = useState({});
  const [subAdmins, setSubAdmins] = useState([]);
  const [editingPackage, setEditingPackage] = useState(null);
  const [loading, setLoading] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL + '/api';

  // Language content
  const content = {
    english: {
      title: "Super Admin Dashboard",
      subtitle: "Rate My Service - System Administration",
      welcome: "Welcome",
      logout: "Logout",
      tabs: {
        general: "General",
        tenants: "Tenant Management",
        admins: "Admin Management",
        settings: "Global Settings",
        packages: "Subscription Packages",
        website: "Website CMS",
        inbox: "Contact Inbox"
      },
      general: {
        successTitle: "🎉 Super Admin Login Working Successfully!",
        successDesc: "You have successfully logged in as Super Administrator. The system is now ready for production deployment.",
        systemStatus: "System Status: Ready for Deployment",
        statusItems: [
          "Super admin authentication working",
          "Database initialized with proper credentials", 
          "Separate login pages implemented",
          "All UI fixes completed"
        ],
        systemInfo: "System Information",
        quickActions: "Quick Actions",
        fields: {
          email: "Super Admin Email",
          name: "Admin Name", 
          role: "Role",
          status: "Status",
          active: "Active"
        },
        actions: {
          tenants: { title: "Tenant Management", desc: "Manage organizations and approvals" },
          admins: { title: "Admin Management", desc: "Manage sub-administrators and assignments" },
          settings: { title: "Global Settings", desc: "Configure system-wide settings" },
          packages: { title: "Subscription Packages", desc: "Manage pricing and features" }
        }
      },
      tenants: {
        pendingRequests: "Pending Registration Requests",
        activeOrganizations: "Active Organizations",
        viewDetails: "View Details",
        deactivate: "Deactivate",
        activate: "Activate", 
        delete: "Delete",
        resetCredentials: "Reset Credentials",
        confirmDelete: "Are you sure you want to permanently delete this organization? This action cannot be undone.",
        confirmDeactivate: "Are you sure you want to deactivate this organization?",
        confirmActivate: "Are you sure you want to activate this organization?",
        confirmResetCredentials: "Are you sure you want to reset admin credentials? A new password will be generated and sent via email.",
        approve: "Approve",
        reject: "Reject",
        noRequests: "No pending registration requests",
        noOrganizations: "No active organizations",
        headers: {
          actions: "Actions",
          package: "Package",
          industry: "Industry", 
          email: "Email",
          orgName: "Organization Name",
          status: "Status",
          employees: "Employees",
          active: "Active",
          inactive: "Inactive"
        }
      },
      settings: {
        title: "Global Settings",
        platformName: "Platform Name",
        platformDesc: "Platform Description",
        supportEmail: "Support Email",
        termsConditionsUrl: "Terms & Conditions URL",
        dataProtectionUrl: "Data Protection Agreement URL",
        saveButton: "Save Settings"
      },
      packages: {
        title: "Subscription Packages",
        headers: {
          status: "Status",
          annualPrice: "Annual Price",
          monthlyPrice: "Monthly Price", 
          reviewLimit: "Review Limit",
          employeeLimit: "Employee Limit",
          packageName: "Package Name",
          active: "Active",
          inactive: "Inactive"
        },
        noPackages: "No subscription packages"
      },
      admins: {
        title: "Admin Management",
        subAdmins: "Sub Administrators",
        addSubAdmin: "Add Sub Admin",
        assignOrganizations: "Assign Organizations",
        manageByIndustry: "Manage by Industry",
        manageIndividually: "Manage Individually",
        noSubAdmins: "No sub administrators",
        headers: {
          name: "Name",
          email: "Email",
          managementType: "Management Type",
          assignedCount: "Assigned Organizations",
          status: "Status",
          actions: "Actions"
        }
      },
      globalSettings: {
        logoManagement: "Logo Management",
        currentLogo: "Current System Logo",
        uploadLogo: "Upload New Logo",
        removeLogo: "Remove Logo",
        logoPreview: "Logo Preview",
        supportedFormats: "Supported formats: PNG, JPG, GIF up to 5MB"
      },
      packageEdit: {
        editPackage: "Edit Package",
        saveChanges: "Save Changes",
        cancel: "Cancel",
        packageName: "Package Name",
        employeeLimit: "Employee Limit",
        reviewLimit: "Review Limit (monthly)",
        monthlyPrice: "Monthly Price (AED)",
        annualPrice: "Annual Price (AED)",
        status: "Status",
        active: "Active",
        inactive: "Inactive"
      }
    },
    arabic: {
      title: "لوحة تحكم المشرف العام",
      subtitle: "Rate My Service - إدارة النظام",
      welcome: "مرحباً",
      logout: "تسجيل الخروج",
      tabs: {
        general: "عام",
        tenants: "إدارة المستأجرين",
        admins: "إدارة المشرفين",
        settings: "الإعدادات العامة",
        packages: "باقات الاشتراك",
        website: "إدارة الموقع",
        inbox: "صندوق الرسائل"
      },
      general: {
        successTitle: "🎉 تم تسجيل الدخول بنجاح كمشرف عام!",
        successDesc: "لقد قمت بتسجيل الدخول بنجاح كمشرف عام. النظام الآن جاهز للنشر في الإنتاج.",
        systemStatus: "حالة النظام: جاهز للنشر",
        statusItems: [
          "مصادقة المشرف العام تعمل",
          "قاعدة البيانات مهيأة بالمعلومات الصحيحة",
          "تم تنفيذ صفحات تسجيل الدخول المنفصلة", 
          "تم إكمال جميع إصلاحات واجهة المستخدم"
        ],
        systemInfo: "معلومات النظام",
        quickActions: "الإجراءات السريعة",
        fields: {
          email: "بريد المشرف العام",
          name: "اسم المشرف",
          role: "الدور",
          status: "الحالة",
          active: "نشط"
        },
        actions: {
          tenants: { title: "إدارة المستأجرين", desc: "إدارة المؤسسات والموافقات" },
          admins: { title: "إدارة المشرفين", desc: "إدارة المشرفين الفرعيين والتخصيصات" },
          settings: { title: "الإعدادات العامة", desc: "تكوين الإعدادات على مستوى النظام" },
          packages: { title: "باقات الاشتراك", desc: "إدارة الأسعار والميزات" }
        }
      },
      tenants: {
        pendingRequests: "طلبات التسجيل المعلقة",
        activeOrganizations: "المؤسسات النشطة", 
        approve: "موافقة",
        reject: "رفض",
        resetCredentials: "إعادة تعيين بيانات الاعتماد",
        confirmResetCredentials: "هل أنت متأكد من أنك تريد إعادة تعيين بيانات اعتماد المشرف؟ سيتم إنشاء كلمة مرور جديدة وإرسالها عبر البريد الإلكتروني.",
        noRequests: "لا توجد طلبات تسجيل معلقة",
        noOrganizations: "لا توجد مؤسسات نشطة",
        headers: {
          actions: "الإجراءات",
          package: "الباقة",
          industry: "الصناعة",
          email: "البريد الإلكتروني", 
          orgName: "اسم المؤسسة",
          status: "الحالة",
          employees: "عدد الموظفين",
          active: "نشط",
          inactive: "غير نشط"
        }
      },
      settings: {
        title: "الإعدادات العامة",
        platformName: "اسم المنصة",
        platformDesc: "وصف المنصة", 
        supportEmail: "بريد الدعم",
        termsConditionsUrl: "رابط الشروط والأحكام",
        dataProtectionUrl: "رابط اتفاقية حماية البيانات",
        saveButton: "حفظ الإعدادات"
      },
      packages: {
        title: "باقات الاشتراك",
        headers: {
          status: "الحالة",
          annualPrice: "السعر السنوي", 
          monthlyPrice: "السعر الشهري",
          reviewLimit: "حد المراجعات",
          employeeLimit: "حد الموظفين",
          packageName: "اسم الباقة",
          active: "نشط",
          inactive: "غير نشط"
        },
        noPackages: "لا توجد باقات اشتراك"
      },
      admins: {
        title: "إدارة المشرفين",
        subAdmins: "المشرفين الفرعيين",
        addSubAdmin: "إضافة مشرف فرعي",
        assignOrganizations: "تخصيص المؤسسات",
        manageByIndustry: "الإدارة حسب الصناعة",
        manageIndividually: "الإدارة الفردية",
        noSubAdmins: "لا يوجد مشرفين فرعيين",
        headers: {
          name: "الاسم",
          email: "البريد الإلكتروني",
          managementType: "نوع الإدارة",
          assignedCount: "المؤسسات المخصصة",
          status: "الحالة",
          actions: "الإجراءات"
        }
      },
      globalSettings: {
        logoManagement: "إدارة الشعار",
        currentLogo: "شعار النظام الحالي",
        uploadLogo: "تحميل شعار جديد",
        removeLogo: "إزالة الشعار",
        logoPreview: "معاينة الشعار",
        supportedFormats: "الصيغ المدعومة: PNG, JPG, GIF حتى 5 ميجابايت"
      },
      packageEdit: {
        editPackage: "تعديل الباقة",
        saveChanges: "حفظ التغييرات",
        cancel: "إلغاء",
        packageName: "اسم الباقة",
        employeeLimit: "حد الموظفين",
        reviewLimit: "حد المراجعات (شهرياً)",
        monthlyPrice: "السعر الشهري (درهم)",
        annualPrice: "السعر السنوي (درهم)",
        status: "الحالة",
        active: "نشط",
        inactive: "غير نشط"
      }
    }
  };

  const t = content[language]; // Translation helper
  const isRTL = language === 'arabic';
  const textAlign = isRTL ? 'text-right' : 'text-left';
  const flexAlign = isRTL ? 'justify-end' : 'justify-start';

  // Refresh data when filter changes
  useEffect(() => {
    if (activeTab === 'tenants') {
      fetchData();
    }
  }, [requestsFilter]);

  // Fetch data when tab changes
  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const headers = { 'Authorization': `Bearer ${token}` };

      if (activeTab === 'tenants') {
        // Fetch organizations and registration requests with proper filtering
        const [orgsResponse, requestsResponse] = await Promise.all([
          axios.get(`${API}/organizations`, { headers }),
          axios.get(`${API}/registration-requests?status=${requestsFilter}`, { headers })
        ]);
        setOrganizations(orgsResponse.data || []);
        // Use API-filtered data directly instead of client-side filtering
        setRegistrationRequests(requestsResponse.data || []);
      } else if (activeTab === 'admins') {
        // Fetch sub-admins
        const adminsResponse = await axios.get(`${API}/sub-admins`, { headers });
        setSubAdmins(adminsResponse.data || []);
      } else if (activeTab === 'packages') {
        // Fetch subscription packages
        const packagesResponse = await axios.get(`${API}/subscription-packages`, { headers });
        setSubscriptionPackages(packagesResponse.data || []);
      } else if (activeTab === 'settings') {
        // Fetch global settings
        const settingsResponse = await axios.get(`${API}/global-settings`, { headers });
        setGlobalSettings(settingsResponse.data || {});
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Add manual refresh function
  const handleManualRefresh = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const headers = { 'Authorization': `Bearer ${token}` };
      const requestsResponse = await axios.get(`${API}/registration-requests?status=${requestsFilter}`, { headers });
      setRegistrationRequests(requestsResponse.data || []);
    } catch (error) {
      console.error('Error refreshing requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRequest = async (requestId) => {
    if (!confirm('Are you sure you want to approve this registration request?')) return;
    
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(`${API}/registration-requests/${requestId}/approve`, {
        status: 'approved',
        approval_notes: 'Approved by Super Admin'
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // Show success message with request refresh
      alert('Registration request approved successfully!');
      await handleManualRefresh(); // Use manual refresh instead of fetchData
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message;
      if (errorMessage.includes('already been processed')) {
        alert('This request has already been processed and will be removed from the list.');
        await handleManualRefresh(); // Refresh to show current state
      } else {
        alert('Error approving request: ' + errorMessage);
      }
    }
  };

  const handleRejectRequest = async (requestId) => {
    const reason = prompt('Please provide a reason for rejection:');
    if (!reason) return; // User cancelled
    
    if (!confirm('Are you sure you want to reject this registration request?')) return;
    
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(`${API}/registration-requests/${requestId}/approve`, {
        status: 'rejected',
        rejection_reason: reason
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert('Registration request rejected');
      await handleManualRefresh(); // Use manual refresh instead of fetchData
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message;
      if (errorMessage.includes('already been processed')) {
        alert('This request has already been processed and will be removed from the list.');
        await handleManualRefresh(); // Refresh to show current state
      } else {
        alert('Error rejecting request: ' + errorMessage);
      }
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert('File size must be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64Logo = e.target.result;
          const token = localStorage.getItem('authToken');
          await axios.put(`${API}/global-settings`, 
            { logo: base64Logo },
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          setGlobalSettings(prev => ({ ...prev, logo: base64Logo }));
          alert('Logo uploaded successfully!');
        } catch (error) {
          alert('Error uploading logo: ' + (error.response?.data?.detail || error.message));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(`${API}/global-settings`, 
        { logo: null },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setGlobalSettings(prev => ({ ...prev, logo: null }));
      alert('Logo removed successfully!');
    } catch (error) {
      alert('Error removing logo: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Organization management functions
  const handleViewOrganizationDetails = async (orgId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API}/organizations/${orgId}/details`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSelectedOrganization(response.data);
      setShowDetailsModal(true);
    } catch (error) {
      alert('Error fetching organization details: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleToggleOrganizationStatus = async (orgId, currentStatus) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    const confirmMessage = currentStatus ? t.tenants.confirmDeactivate : t.tenants.confirmActivate;
    
    if (!confirm(confirmMessage)) return;
    
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(`${API}/organizations/${orgId}/toggle-status`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert(`Organization ${action}d successfully!`);
      fetchData(); // Refresh data
    } catch (error) {
      alert(`Error ${action}ing organization: ` + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteOrganization = async (orgId, orgName) => {
    const confirmMessage = `${t.tenants.confirmDelete}\n\nOrganization: ${orgName}`;
    
    if (!confirm(confirmMessage)) return;
    
    // Double confirmation for permanent deletion
    if (!confirm('This will PERMANENTLY delete ALL data for this organization. Type "DELETE" to confirm.')) return;
    
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${API}/organizations/${orgId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert('Organization deleted permanently!');
      fetchData(); // Refresh data
    } catch (error) {
      alert('Error deleting organization: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleResetCredentials = async (orgId, orgName) => {
    // Prompt for an optional custom password. Blank = auto-generate (existing behavior).
    const customPassword = window.prompt(
      `Reset admin credentials for ${orgName}?\n\n` +
      `Leave blank to auto-generate a secure random password (will be emailed to the admin).\n` +
      `Or type a custom password (min 6 characters) to set it directly.`,
      ''
    );
    if (customPassword === null) return; // user cancelled

    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const trimmed = (customPassword || '').trim();
      const body = trimmed ? { new_password: trimmed } : {};
      const response = await axios.post(
        `${API}/organizations/${orgId}/reset-admin-password`,
        body,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      const usedCustom = response.data.password_source === 'custom';
      if (response.data.email_sent) {
        alert(
          `✅ Admin credentials reset for ${orgName}.\n\n` +
          (usedCustom
            ? `Password was set to the value you entered. A confirmation email has been sent to ${response.data.admin_email}.`
            : `A new auto-generated password has been emailed to ${response.data.admin_email}.`)
        );
      } else {
        alert(
          `⚠️ Admin credentials reset for ${orgName}.\n\n` +
          `Email delivery failed. ` +
          (usedCustom
            ? `Password is the value you just entered.`
            : `New password: ${response.data.new_password || '(check server logs)'}`) +
          `\n\nPlease share it with the admin manually.`
        );
      }
      await fetchData();
    } catch (error) {
      console.error('Error resetting credentials:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to reset credentials';
      alert(`❌ Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPackage = (pkg) => {
    setEditingPackage({ ...pkg });
  };

  const handleSavePackage = async () => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(`${API}/subscription-packages/${editingPackage.id}`, editingPackage, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Update local state
      setSubscriptionPackages(prev => 
        prev.map(pkg => pkg.id === editingPackage.id ? editingPackage : pkg)
      );
      setEditingPackage(null);
      alert('Package updated successfully!');
    } catch (error) {
      alert('Error updating package: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCancelEdit = () => {
    setEditingPackage(null);
  };

  const TabButton = ({ tabKey, title, isActive, onClick }) => (
    <button
      onClick={() => onClick(tabKey)}
      className={`px-6 py-3 text-sm font-medium transition-colors ${
        isActive
          ? 'border-b-2 border-red-500 text-red-600 bg-red-50'
          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
      }`}
    >
      {title}
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className={`${isRTL ? 'mr-4' : 'ml-4'}`}>
                <h1 className={`text-xl font-semibold text-gray-900 ${textAlign}`}>{t.title}</h1>
                <p className={`text-sm text-gray-500 ${textAlign}`}>{t.subtitle}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {/* Language Switch */}
              <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setLanguage('english')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    language === 'english'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  English
                </button>
                <button
                  onClick={() => setLanguage('arabic')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    language === 'arabic'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  العربية
                </button>
              </div>
              
              <div className={`text-sm text-gray-700 ${textAlign}`}>
                {t.welcome}, <span className="font-medium">{user?.name}</span>
              </div>
              <button
                onClick={logout}
                className="bg-red-600 text-white px-4 py-2 rounded-md text-sm hover:bg-red-700 transition-colors"
              >
                {t.logout}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className={`flex space-x-8 ${isRTL ? 'flex-row-reverse space-x-reverse' : ''}`}>
            <TabButton
              tabKey="general"
              title={t.tabs.general}
              isActive={activeTab === 'general'}
              onClick={setActiveTab}
            />
            <TabButton
              tabKey="tenants"
              title={t.tabs.tenants}
              isActive={activeTab === 'tenants'}
              onClick={setActiveTab}
            />
            <TabButton
              tabKey="admins"
              title={t.tabs.admins}
              isActive={activeTab === 'admins'}
              onClick={setActiveTab}
            />
            <TabButton
              tabKey="settings"
              title={t.tabs.settings}
              isActive={activeTab === 'settings'}
              onClick={setActiveTab}
            />
            <TabButton
              tabKey="packages"
              title={t.tabs.packages}
              isActive={activeTab === 'packages'}
              onClick={setActiveTab}
            />
            <TabButton
              tabKey="website"
              title={t.tabs.website}
              isActive={activeTab === 'website'}
              onClick={setActiveTab}
            />
            <TabButton
              tabKey="inbox"
              title={t.tabs.inbox}
              isActive={activeTab === 'inbox'}
              onClick={setActiveTab}
            />
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
            </div>
          )}

          {/* General Tab */}
          {activeTab === 'general' && (
            <>
              {/* Welcome Section */}
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h2 className={`text-lg font-medium text-gray-900 mb-2 ${textAlign}`}>
                  {t.general.successTitle}
                </h2>
                <p className={`text-gray-600 ${textAlign}`}>
                  {t.general.successDesc}
                </p>
                <div className="mt-4 bg-green-50 border border-green-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className={`${isRTL ? 'mr-3' : 'ml-3'}`}>
                      <h3 className={`text-sm font-medium text-green-800 ${textAlign}`}>
                        {t.general.systemStatus}
                      </h3>
                      <div className="mt-2 text-sm text-green-700">
                        <ul className={`space-y-1 ${textAlign}`}>
                          {t.general.statusItems.map((item, index) => (
                            <li key={index}>✅ {item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* System Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className={`text-lg font-medium text-gray-900 mb-4 ${textAlign}`}>{t.general.systemInfo}</h3>
                  <dl className="space-y-2">
                    <div>
                      <dt className={`text-sm font-medium text-gray-500 ${textAlign}`}>{t.general.fields.email}</dt>
                      <dd className={`text-sm text-gray-900 ${textAlign}`}>{user?.email}</dd>
                    </div>
                    <div>
                      <dt className={`text-sm font-medium text-gray-500 ${textAlign}`}>{t.general.fields.name}</dt>
                      <dd className={`text-sm text-gray-900 ${textAlign}`}>{user?.name}</dd>
                    </div>
                    <div>
                      <dt className={`text-sm font-medium text-gray-500 ${textAlign}`}>{t.general.fields.role}</dt>
                      <dd className={`text-sm text-gray-900 capitalize ${textAlign}`}>{user?.role?.replace('_', ' ')}</dd>
                    </div>
                    <div>
                      <dt className={`text-sm font-medium text-gray-500 ${textAlign}`}>{t.general.fields.status}</dt>
                      <dd className={`text-sm ${textAlign}`}>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {t.general.fields.active}
                        </span>
                      </dd>
                    </div>
                  </dl>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className={`text-lg font-medium text-gray-900 mb-4 ${textAlign}`}>{t.general.quickActions}</h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => setActiveTab('tenants')}
                      className={`w-full px-4 py-3 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors ${textAlign}`}
                    >
                      <div className={`font-medium text-blue-900 ${textAlign}`}>{t.general.actions.tenants.title}</div>
                      <div className={`text-sm text-blue-700 ${textAlign}`}>{t.general.actions.tenants.desc}</div>
                    </button>
                    <button
                      onClick={() => setActiveTab('admins')}
                      className={`w-full px-4 py-3 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors ${textAlign}`}
                    >
                      <div className={`font-medium text-indigo-900 ${textAlign}`}>{t.general.actions.admins.title}</div>
                      <div className={`text-sm text-indigo-700 ${textAlign}`}>{t.general.actions.admins.desc}</div>
                    </button>
                    <button
                      onClick={() => setActiveTab('settings')}
                      className={`w-full px-4 py-3 bg-purple-50 hover:bg-purple-100 rounded-md transition-colors ${textAlign}`}
                    >
                      <div className={`font-medium text-purple-900 ${textAlign}`}>{t.general.actions.settings.title}</div>
                      <div className={`text-sm text-purple-700 ${textAlign}`}>{t.general.actions.settings.desc}</div>
                    </button>
                    <button
                      onClick={() => setActiveTab('packages')}
                      className={`w-full px-4 py-3 bg-green-50 hover:bg-green-100 rounded-md transition-colors ${textAlign}`}
                    >
                      <div className={`font-medium text-green-900 ${textAlign}`}>{t.general.actions.packages.title}</div>
                      <div className={`text-sm text-green-700 ${textAlign}`}>{t.general.actions.packages.desc}</div>
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Tenant Management Tab */}
          {activeTab === 'tenants' && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    <h3 className={`text-lg font-medium text-gray-900 ${textAlign}`}>
                      {t.tenants.pendingRequests} ({registrationRequests.length})
                    </h3>
                    <div className="flex items-center space-x-3">
                      <select
                        value={requestsFilter}
                        onChange={(e) => setRequestsFilter(e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="">All Requests</option>
                      </select>
                      <button
                        onClick={handleManualRefresh}
                        disabled={loading}
                        className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                      >
                        {loading ? '↻' : '🔄'} Refresh
                      </button>
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className={`px-6 py-3 ${textAlign} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t.tenants.headers.status}</th>
                        <th className={`px-6 py-3 ${textAlign} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t.tenants.headers.actions}</th>
                        <th className={`px-6 py-3 ${textAlign} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t.tenants.headers.package}</th>
                        <th className={`px-6 py-3 ${textAlign} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t.tenants.headers.industry}</th>
                        <th className={`px-6 py-3 ${textAlign} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t.tenants.headers.email}</th>
                        <th className={`px-6 py-3 ${textAlign} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t.tenants.headers.orgName}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {registrationRequests.map((request) => (
                        <tr key={request.id}>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${textAlign}`}>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              request.status === 'approved' ? 'bg-green-100 text-green-800' :
                              request.status === 'rejected' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                            </span>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${textAlign}`}>
                            {request.status === 'pending' && (
                              <div className={`flex space-x-2 ${flexAlign}`}>
                                <button
                                  onClick={() => handleRejectRequest(request.id)}
                                  className="bg-red-600 text-white px-3 py-1 rounded text-xs hover:bg-red-700"
                                >
                                  {t.tenants.reject}
                                </button>
                                <button
                                  onClick={() => handleApproveRequest(request.id)}
                                  className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700"
                                >
                                  {t.tenants.approve}
                                </button>
                              </div>
                            )}
                            {request.status !== 'pending' && (
                              <span className="text-gray-400 text-xs">No actions available</span>
                            )}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${textAlign}`}>{request.package_name}</td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${textAlign}`}>{request.industry}</td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${textAlign}`}>{request.admin_email}</td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 ${textAlign}`}>{request.organization_name}</td>
                        </tr>
                      ))}
                      {registrationRequests.length === 0 && (
                        <tr>
                          <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                            {requestsFilter === 'pending' ? t.tenants.noRequests : `No ${requestsFilter || 'registration'} requests found`}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className={`text-lg font-medium text-gray-900 ${textAlign}`}>{t.tenants.activeOrganizations}</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className={`px-6 py-3 ${textAlign} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t.tenants.headers.actions}</th>
                        <th className={`px-6 py-3 ${textAlign} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t.tenants.headers.status}</th>
                        <th className={`px-6 py-3 ${textAlign} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t.tenants.headers.package}</th>
                        <th className={`px-6 py-3 ${textAlign} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t.tenants.headers.employees}</th>
                        <th className={`px-6 py-3 ${textAlign} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t.tenants.headers.industry}</th>
                        <th className={`px-6 py-3 ${textAlign} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t.tenants.headers.orgName}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {organizations.map((org) => (
                        <tr key={org.id}>
                          <td className={`px-6 py-4 whitespace-nowrap ${textAlign}`}>
                            <div className={`flex space-x-1 ${flexAlign}`}>
                              <button
                                onClick={() => setSelectedOrganization(org)}
                                className="bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700"
                                title={t.tenants.viewDetails}
                              >
                                👁️
                              </button>
                              <button
                                onClick={() => handleResetCredentials(org.id, org.name)}
                                className="bg-purple-600 text-white px-3 py-1 rounded text-xs hover:bg-purple-700"
                                title={t.tenants.resetCredentials}
                              >
                                🔑
                              </button>
                              <button
                                onClick={() => handleToggleOrganizationStatus(org.id, !org.is_active, org.name)}
                                className={`${org.is_active ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'} text-white px-3 py-1 rounded text-xs`}
                                title={org.is_active ? t.tenants.deactivate : t.tenants.activate}
                              >
                                {org.is_active ? '⏸️' : '▶️'}
                              </button>
                              <button
                                onClick={() => handleDeleteOrganization(org.id, org.name)}
                                className="bg-red-600 text-white px-3 py-1 rounded text-xs hover:bg-red-700"
                                title={t.tenants.delete}
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap ${textAlign}`}>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              org.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {org.is_active ? t.tenants.headers.active : t.tenants.headers.inactive}
                            </span>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${textAlign}`}>{org.package_name}</td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${textAlign}`}>{org.staff_count}</td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${textAlign}`}>{org.industry}</td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 ${textAlign}`}>{org.name}</td>
                        </tr>
                      ))}
                      {organizations.length === 0 && (
                        <tr>
                          <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                            {t.tenants.noOrganizations}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Admin Management Tab */}
          {activeTab === 'admins' && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <h3 className={`text-lg font-medium text-gray-900 ${textAlign}`}>{t.admins.title}</h3>
                  <button className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700">
                    {t.admins.addSubAdmin}
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className={`px-6 py-3 ${textAlign} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t.admins.headers.actions}</th>
                        <th className={`px-6 py-3 ${textAlign} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t.admins.headers.status}</th>
                        <th className={`px-6 py-3 ${textAlign} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t.admins.headers.assignedCount}</th>
                        <th className={`px-6 py-3 ${textAlign} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t.admins.headers.managementType}</th>
                        <th className={`px-6 py-3 ${textAlign} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t.admins.headers.email}</th>
                        <th className={`px-6 py-3 ${textAlign} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t.admins.headers.name}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {subAdmins.map((admin) => (
                        <tr key={admin.id}>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${textAlign}`}>
                            <div className={`flex space-x-2 ${flexAlign}`}>
                              <button className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700">
                                {t.admins.assignOrganizations}
                              </button>
                              <button className="bg-red-600 text-white px-3 py-1 rounded text-xs hover:bg-red-700">
                                Remove
                              </button>
                            </div>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap ${textAlign}`}>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              admin.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {admin.is_active ? t.tenants.headers.active : t.tenants.headers.inactive}
                            </span>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${textAlign}`}>{admin.assigned_organizations || 0}</td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${textAlign}`}>
                            {admin.management_type === 'industry' ? t.admins.manageByIndustry : t.admins.manageIndividually}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${textAlign}`}>{admin.email}</td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 ${textAlign}`}>{admin.name}</td>
                        </tr>
                      ))}
                      {subAdmins.length === 0 && (
                        <tr>
                          <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                            {t.admins.noSubAdmins}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Global Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className={`text-lg font-medium text-gray-900 mb-4 ${textAlign}`}>{t.settings.title}</h3>
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-2 ${textAlign}`}>{t.settings.platformName}</label>
                    <input
                      type="text"
                      defaultValue={globalSettings.platform_name || 'Rate My Service'}
                      className={`w-full border border-gray-300 rounded-md px-3 py-2 ${textAlign}`}
                      dir={isRTL ? 'rtl' : 'ltr'}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-2 ${textAlign}`}>{t.settings.platformDesc}</label>
                    <textarea
                      defaultValue={globalSettings.platform_description || (isRTL ? 'منصة إدارة تقييم العملاء متعددة المستأجرين' : 'Multi-tenant customer feedback management platform')}
                      className={`w-full border border-gray-300 rounded-md px-3 py-2 ${textAlign}`}
                      rows="3"
                      dir={isRTL ? 'rtl' : 'ltr'}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-2 ${textAlign}`}>{t.settings.supportEmail}</label>
                    <input
                      type="email"
                      defaultValue={globalSettings.support_email || 'support@ratemyservice.com'}
                      className={`w-full border border-gray-300 rounded-md px-3 py-2 ${textAlign}`}
                      dir="ltr"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-2 ${textAlign}`}>{t.settings.termsConditionsUrl}</label>
                    <input
                      type="url"
                      defaultValue={globalSettings.terms_conditions_url || ''}
                      className={`w-full border border-gray-300 rounded-md px-3 py-2 ${textAlign}`}
                      placeholder="https://example.com/terms-and-conditions"
                      dir="ltr"
                    />
                    <p className="text-xs text-gray-500 mt-1">Enter the full URL to your Terms and Conditions page</p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-2 ${textAlign}`}>{t.settings.dataProtectionUrl}</label>
                    <input
                      type="url"
                      defaultValue={globalSettings.data_protection_url || ''}
                      className={`w-full border border-gray-300 rounded-md px-3 py-2 ${textAlign}`}
                      placeholder="https://example.com/data-protection-agreement"
                      dir="ltr"
                    />
                    <p className="text-xs text-gray-500 mt-1">Enter the full URL to your Data Protection Agreement page</p>
                  </div>
                  <div className="pt-4">
                    <button className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700">
                      {t.settings.saveButton}
                    </button>
                  </div>
                </div>
              </div>

              {/* Logo Management Section */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className={`text-lg font-medium text-gray-900 mb-4 ${textAlign}`}>{t.globalSettings.logoManagement}</h3>
                <div className="space-y-4">
                  {globalSettings.logo ? (
                    <div>
                      <label className={`block text-sm font-medium text-gray-700 mb-2 ${textAlign}`}>{t.globalSettings.currentLogo}</label>
                      <div className="border-2 border-gray-300 rounded-lg p-4">
                        <img
                          src={globalSettings.logo}
                          alt="System Logo"
                          className="h-24 object-contain mx-auto mb-4"
                        />
                        <div className={`flex space-x-2 ${flexAlign}`}>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                            id="global-logo-upload"
                          />
                          <label
                            htmlFor="global-logo-upload"
                            className="cursor-pointer bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition-colors text-sm"
                          >
                            {t.globalSettings.uploadLogo}
                          </label>
                          <button
                            onClick={handleRemoveLogo}
                            className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 transition-colors text-sm"
                          >
                            {t.globalSettings.removeLogo}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className={`block text-sm font-medium text-gray-700 mb-2 ${textAlign}`}>{t.globalSettings.uploadLogo}</label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                        <div className="text-center">
                          <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <div className="mt-4">
                            <label htmlFor="global-logo-upload" className="cursor-pointer">
                              <span className="mt-2 block text-sm font-medium text-gray-900">
                                {t.globalSettings.uploadLogo}
                              </span>
                              <span className="mt-1 block text-sm text-gray-500">
                                {t.globalSettings.supportedFormats}
                              </span>
                            </label>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleLogoUpload}
                              className="hidden"
                              id="global-logo-upload"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Subscription Packages Tab */}
          {activeTab === 'packages' && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className={`text-lg font-medium text-gray-900 ${textAlign}`}>{t.packages.title}</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className={`px-6 py-3 ${textAlign} text-xs font-medium text-gray-500 uppercase tracking-wider`}>Actions</th>
                      <th className={`px-6 py-3 ${textAlign} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t.packages.headers.status}</th>
                      <th className={`px-6 py-3 ${textAlign} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t.packages.headers.annualPrice}</th>
                      <th className={`px-6 py-3 ${textAlign} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t.packages.headers.monthlyPrice}</th>
                      <th className={`px-6 py-3 ${textAlign} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t.packages.headers.reviewLimit}</th>
                      <th className={`px-6 py-3 ${textAlign} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t.packages.headers.employeeLimit}</th>
                      <th className={`px-6 py-3 ${textAlign} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t.packages.headers.packageName}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {subscriptionPackages.map((pkg) => (
                      <tr key={pkg.id}>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${textAlign}`}>
                          {editingPackage?.id === pkg.id ? (
                            <div className={`flex space-x-2 ${flexAlign}`}>
                              <button
                                onClick={handleSavePackage}
                                className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700"
                              >
                                {t.packageEdit.saveChanges}
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="bg-gray-600 text-white px-3 py-1 rounded text-xs hover:bg-gray-700"
                              >
                                {t.packageEdit.cancel}
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleEditPackage(pkg)}
                              className="bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700"
                            >
                              Edit
                            </button>
                          )}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap ${textAlign}`}>
                          {editingPackage?.id === pkg.id ? (
                            <select
                              value={editingPackage.is_active}
                              onChange={(e) => setEditingPackage({...editingPackage, is_active: e.target.value === 'true'})}
                              className="border border-gray-300 rounded px-2 py-1 text-xs"
                            >
                              <option value="true">{t.packageEdit.active}</option>
                              <option value="false">{t.packageEdit.inactive}</option>
                            </select>
                          ) : (
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              pkg.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {pkg.is_active ? t.packages.headers.active : t.packages.headers.inactive}
                            </span>
                          )}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${textAlign}`}>
                          {editingPackage?.id === pkg.id ? (
                            <input
                              type="number"
                              value={editingPackage.annual_price_aed}
                              onChange={(e) => setEditingPackage({...editingPackage, annual_price_aed: parseFloat(e.target.value)})}
                              className="w-20 border border-gray-300 rounded px-2 py-1 text-xs"
                            />
                          ) : (
                            `${pkg.annual_price_aed} ${isRTL ? 'درهم' : 'AED'}`
                          )}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${textAlign}`}>
                          {editingPackage?.id === pkg.id ? (
                            <input
                              type="number"
                              value={editingPackage.monthly_price_aed}
                              onChange={(e) => setEditingPackage({...editingPackage, monthly_price_aed: parseFloat(e.target.value)})}
                              className="w-20 border border-gray-300 rounded px-2 py-1 text-xs"
                            />
                          ) : (
                            `${pkg.monthly_price_aed} ${isRTL ? 'درهم' : 'AED'}`
                          )}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${textAlign}`}>
                          {editingPackage?.id === pkg.id ? (
                            <input
                              type="number"
                              value={editingPackage.max_monthly_reviews}
                              onChange={(e) => setEditingPackage({...editingPackage, max_monthly_reviews: parseInt(e.target.value)})}
                              className="w-20 border border-gray-300 rounded px-2 py-1 text-xs"
                            />
                          ) : (
                            pkg.max_monthly_reviews
                          )}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${textAlign}`}>
                          {editingPackage?.id === pkg.id ? (
                            <input
                              type="number"
                              value={editingPackage.max_employees}
                              onChange={(e) => setEditingPackage({...editingPackage, max_employees: parseInt(e.target.value)})}
                              className="w-20 border border-gray-300 rounded px-2 py-1 text-xs"
                            />
                          ) : (
                            pkg.max_employees
                          )}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 ${textAlign}`}>
                          {editingPackage?.id === pkg.id ? (
                            <input
                              type="text"
                              value={editingPackage.name}
                              onChange={(e) => setEditingPackage({...editingPackage, name: e.target.value})}
                              className="w-32 border border-gray-300 rounded px-2 py-1 text-xs"
                            />
                          ) : (
                            pkg.name
                          )}
                        </td>
                      </tr>
                    ))}
                    {subscriptionPackages.length === 0 && (
                      <tr>
                        <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                          {t.packages.noPackages}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Website CMS Tab */}
          {activeTab === 'website' && (
            <WebsiteCMS token={localStorage.getItem('authToken')} />
          )}

          {/* Contact Inbox Tab */}
          {activeTab === 'inbox' && (
            <ContactInbox token={localStorage.getItem('authToken')} />
          )}
        </div>
      </div>

      {/* Organization Details Modal */}
      {showDetailsModal && selectedOrganization && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Organization Details</h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-700 border-b pb-1">Basic Information</h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Organization Name</label>
                    <p className="text-sm text-gray-900">{selectedOrganization.name}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Slug</label>
                    <p className="text-sm text-gray-900">{selectedOrganization.slug}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Industry</label>
                    <p className="text-sm text-gray-900">{selectedOrganization.industry}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Location</label>
                    <p className="text-sm text-gray-900">{selectedOrganization.location}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Phone</label>
                    <p className="text-sm text-gray-900">{selectedOrganization.phone_number}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Website</label>
                    <p className="text-sm text-gray-900">{selectedOrganization.website}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Official Email</label>
                    <p className="text-sm text-gray-900">{selectedOrganization.official_email}</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-700 border-b pb-1">Admin & Statistics</h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Admin Name</label>
                    <p className="text-sm text-gray-900">{selectedOrganization.admin_name || 'N/A'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Admin Email</label>
                    <p className="text-sm text-gray-900">{selectedOrganization.admin_email}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Employee Count</label>
                    <p className="text-sm text-gray-900">{selectedOrganization.employee_count}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Total Feedback</label>
                    <p className="text-sm text-gray-900">{selectedOrganization.feedback_count}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Package</label>
                    <p className="text-sm text-gray-900">{selectedOrganization.package_name}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Status</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      selectedOrganization.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {selectedOrganization.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Subscription Status</label>
                    <p className="text-sm text-gray-900 capitalize">{selectedOrganization.subscription_status}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Created At</label>
                    <p className="text-sm text-gray-900">
                      {selectedOrganization.created_at ? new Date(selectedOrganization.created_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
                
                <div className="col-span-1 md:col-span-2 space-y-3">
                  <h4 className="font-semibold text-gray-700 border-b pb-1">Settings</h4>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <label className="block text-sm font-medium text-gray-600">Language</label>
                      <p className="text-gray-900 capitalize">{selectedOrganization.settings?.preferred_language}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600">Daily Review Limit</label>
                      <p className="text-gray-900">{selectedOrganization.settings?.daily_review_limit}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600">Bad Review Threshold</label>
                      <p className="text-gray-900">{selectedOrganization.settings?.bad_review_threshold} stars</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600">Geofencing</label>
                      <p className="text-gray-900">{selectedOrganization.settings?.geofencing_enabled ? 'Enabled' : 'Disabled'}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;